import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { apiRequest } from '../utils/api';
import { buildOCCSymbol } from '../utils/optionSymbol';

const OPTION_INSTRUCTIONS = [
  { value: 'BUY_TO_OPEN', label: 'Buy to Open' },
  { value: 'SELL_TO_OPEN', label: 'Sell to Open' },
  { value: 'BUY_TO_CLOSE', label: 'Buy to Close' },
  { value: 'SELL_TO_CLOSE', label: 'Sell to Close' },
];

const ORDER_TYPES = [
  { value: 'LIMIT', label: 'Limit' },
  { value: 'MARKET', label: 'Market' },
];

const TIME_IN_FORCE = [
  { value: 'gfd', label: 'Good for Day' },
  { value: 'gtc', label: 'Good Till Canceled' },
];

export default function RobinhoodOrderTest() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountsLoading, setAccountsLoading] = useState(true);

  const [schwabAccounts, setSchwabAccounts] = useState([]);
  const [selectedSchwabAccountId, setSelectedSchwabAccountId] = useState('');

  const [underlying, setUnderlying] = useState('');
  const [expiration, setExpiration] = useState('');
  const [strike, setStrike] = useState('');
  const [putCall, setPutCall] = useState('C');
  const [instruction, setInstruction] = useState('BUY_TO_OPEN');
  const [quantity, setQuantity] = useState(1);
  const [orderType, setOrderType] = useState('LIMIT');
  const [price, setPrice] = useState('');
  const [timeInForce, setTimeInForce] = useState('gfd');

  const [result, setResult] = useState(null);
  const [previewSpec, setPreviewSpec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const [legQuotes, setLegQuotes] = useState({});
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [autoUpdatePrice, setAutoUpdatePrice] = useState(true);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const quoteIntervalRef = useRef(null);

  // Fetch Robinhood MCP + direct-API accounts
  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      // Fetch Robinhood MCP accounts
      let mcpAccs = [];
      try {
        const response = await apiRequest('/api/mcp/robinhood/accounts');
        mcpAccs = (response.data?.accounts || []).map(a => ({ ...a, accountType: 'robinhood-mcp' }));
      } catch (e) {
        // MCP may not be configured
      }

      // Fetch Robinhood direct-API accounts
      let directAccs = [];
      try {
        const allAccsResp = await apiRequest('/api/accounts');
        const rhDirect = (allAccsResp.data || allAccsResp || []).filter(
          a => ['robinhood', 'robinhood-api'].includes(String(a.apiType || a.api_type || '').toLowerCase())
        );
        directAccs = rhDirect.map(a => ({
          id: String(a.id),
          name: a.name,
          accountType: 'robinhood',
        }));
      } catch (e) {
        // No direct-API accounts
      }

      const accs = [...mcpAccs, ...directAccs];
      setAccounts(accs);
      if (accs.length > 0 && !selectedAccountId) {
        setSelectedAccountId(accs[0].id);
      }
    } catch (err) {
      setError('Failed to load Robinhood accounts: ' + err.message);
    } finally {
      setAccountsLoading(false);
    }
  }, [selectedAccountId]);

  // Fetch Schwab accounts (for quotes)
  const fetchSchwabAccounts = useCallback(async () => {
    try {
      const response = await apiRequest('/api/schwab-api/accounts');
      const accs = response.data?.accounts || [];
      setSchwabAccounts(accs);
      if (accs.length > 0 && !selectedSchwabAccountId) {
        setSelectedSchwabAccountId(accs[0].id);
      }
    } catch (err) {
      // Non-critical, quotes just won't work
    }
  }, [selectedSchwabAccountId]);

  useEffect(() => {
    fetchAccounts();
    fetchSchwabAccounts();
  }, [fetchAccounts, fetchSchwabAccounts]);

  // Fetch recent orders
  const fetchRecentOrders = useCallback(async () => {
    if (!selectedAccountId) return;
    setOrdersLoading(true);
    try {
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isDirect = selectedAcc?.accountType === 'robinhood';
      let response;
      if (isDirect) {
        response = await apiRequest(`/api/robinhood-api/orders/${selectedAccountId}`);
      } else {
        response = await apiRequest(`/api/mcp/robinhood/orders?accountId=${selectedAccountId}`);
      }
      setRecentOrders(response.data?.orders || []);
    } catch (err) {
      // non-critical
    } finally {
      setOrdersLoading(false);
    }
  }, [selectedAccountId, accounts]);

  useEffect(() => {
    if (selectedAccountId) {
      fetchRecentOrders();
    }
  }, [selectedAccountId, fetchRecentOrders]);

  // Build the OCC symbol from form fields
  const builtSymbol = useCallback(() => {
    if (!underlying || !expiration || !strike) return null;
    return buildOCCSymbol(underlying, expiration, putCall, parseFloat(strike));
  }, [underlying, expiration, strike, putCall]);

  // Fetch quotes from Schwab
  const fetchQuotes = useCallback(async () => {
    if (!selectedSchwabAccountId || orderSubmitted) return;
    const sym = builtSymbol();
    if (!sym) return;

    setQuotesLoading(true);
    try {
      const response = await apiRequest('/api/schwab-api/quotes', {
        method: 'POST',
        body: JSON.stringify({ accountId: selectedSchwabAccountId, symbols: [sym] }),
      });
      const quotes = response.data?.quotes || {};
      setLegQuotes(quotes);
      setQuoteError(null);

      if (autoUpdatePrice && orderType === 'LIMIT') {
        const symKey = sym.replace(/\s+/g, '');
        const q = quotes[symKey];
        if (q && q.mid) {
          const rounded = Math.round(q.mid * 100) / 100;
          setPrice(String(rounded));
        }
      }
    } catch (err) {
      setQuoteError(err.message || 'Failed to fetch quotes');
    } finally {
      setQuotesLoading(false);
    }
  }, [selectedSchwabAccountId, builtSymbol, autoUpdatePrice, orderType, orderSubmitted]);

  // Poll quotes every 1 second
  useEffect(() => {
    if (quoteIntervalRef.current) clearInterval(quoteIntervalRef.current);
    if (selectedSchwabAccountId && builtSymbol() && !orderSubmitted) {
      quoteIntervalRef.current = setInterval(fetchQuotes, 1000);
    }
    return () => {
      if (quoteIntervalRef.current) clearInterval(quoteIntervalRef.current);
    };
  }, [fetchQuotes, selectedSchwabAccountId, builtSymbol, orderSubmitted]);

  const handlePreview = () => {
    setError(null);
    setPreviewSpec(null);

    const sym = builtSymbol();
    if (!sym) {
      setError('Please fill in underlying, expiration, and strike');
      return;
    }

    const spec = {
      account_number: '(from account)',
      legs: [
        {
          option_id: '(resolved at placement)',
          side: instruction.startsWith('BUY') ? 'buy' : 'sell',
          position_effect: instruction.endsWith('OPEN') ? 'open' : 'close',
          ratio_quantity: 1,
        },
      ],
      quantity: String(parseInt(quantity) || 1),
      type: orderType === 'MARKET' ? 'market' : 'limit',
      time_in_force: timeInForce,
      ...(orderType === 'LIMIT' ? { price: String(Math.abs(parseFloat(price) || 0)) } : {}),
      _occ_symbol: sym,
    };

    setPreviewSpec(spec);
  };

  const handlePlaceOrder = async () => {
    setError(null);
    setResult(null);
    setPreviewSpec(null);

    if (!selectedAccountId) {
      setError('Please select a Robinhood account');
      return;
    }

    const sym = builtSymbol();
    if (!sym) {
      setError('Please fill in underlying, expiration, and strike');
      return;
    }

    setOrderSubmitted(true);
    setLoading(true);
    try {
      const body = {
        accountId: selectedAccountId,
        legs: [{ symbol: sym, instruction, quantity: parseInt(quantity) || 1 }],
        orderType,
        price: orderType === 'LIMIT' ? parseFloat(price) : undefined,
        timeInForce,
      };
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isDirect = selectedAcc?.accountType === 'robinhood';
      const endpoint = isDirect
        ? `/api/robinhood-api/orders/place/${selectedAccountId}`
        : '/api/mcp/robinhood/orders/place';
      const response = await apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setResult(response.data);
      if (response.data?.success) {
        fetchRecentOrders();
      } else {
        const detail = response.data?.message || 'Unknown error';
        setError(`Order placement failed: ${detail}`);
      }
    } catch (err) {
      const detail = err.response?.data?.error || err.response?.data?.message || err.response?.data?.details || err.message || 'Unknown error';
      setError('Order placement failed: ' + detail);
    } finally {
      setLoading(false);
      setOrderSubmitted(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!orderId) return;
    try {
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isDirect = selectedAcc?.accountType === 'robinhood';
      if (isDirect) {
        setError('Cancel not supported for Robinhood direct-API accounts');
        return;
      }
      await apiRequest(`/api/mcp/robinhood/orders/${orderId}/cancel`, {
        method: 'POST',
      });
      fetchRecentOrders();
    } catch (err) {
      setError('Cancel failed: ' + (err.message || 'Unknown error'));
    }
  };

  const sym = builtSymbol();
  const symKey = sym ? sym.replace(/\s+/g, '') : '';
  const q = legQuotes[symKey];
  const isBuy = instruction === 'BUY_TO_OPEN' || instruction === 'BUY_TO_CLOSE';

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Place single-leg option orders via Robinhood MCP. Quotes are fetched from Schwab.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert
          severity={result.success ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setResult(null)}
        >
          {result.success
            ? `Order placed successfully! Order ID: ${result.orderId || 'N/A'}`
            : `Order failed: ${result.message || 'Unknown error'}`}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left column: Order form */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Configuration
              </Typography>

              {/* Account selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Robinhood Account</InputLabel>
                <Select
                  value={selectedAccountId}
                  label="Robinhood Account"
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  disabled={accountsLoading}
                >
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.name} {acc.accountType === 'robinhood' ? '(Robinhood)' : '(MCP)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Schwab account for quotes */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Schwab Account (for quotes)</InputLabel>
                <Select
                  value={selectedSchwabAccountId}
                  label="Schwab Account (for quotes)"
                  onChange={(e) => setSelectedSchwabAccountId(e.target.value)}
                  disabled={schwabAccounts.length === 0}
                >
                  {schwabAccounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.name} ({acc.accountHash?.slice(0, 8)}...)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Option specification */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Underlying"
                    value={underlying}
                    onChange={(e) => setUnderlying(e.target.value.toUpperCase())}
                    placeholder="e.g. SPY"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Expiration"
                    value={expiration}
                    onChange={(e) => setExpiration(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Strike"
                    value={strike}
                    onChange={(e) => setStrike(e.target.value)}
                    placeholder="e.g. 150"
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={putCall}
                      label="Type"
                      onChange={(e) => setPutCall(e.target.value)}
                    >
                      <MenuItem value="C">Call</MenuItem>
                      <MenuItem value="P">Put</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <FormControl fullWidth>
                    <InputLabel>Side</InputLabel>
                    <Select
                      value={instruction}
                      label="Side"
                      onChange={(e) => setInstruction(e.target.value)}
                    >
                      {OPTION_INSTRUCTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* OCC symbol preview */}
              {sym && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontFamily: 'monospace' }}>
                  OCC Symbol: {sym}
                </Typography>
              )}

              {/* Real-time quotes display */}
              {sym && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">
                      Live Quote (via Schwab)
                    </Typography>
                    {quotesLoading && <CircularProgress size={14} />}
                  </Box>
                  {quoteError && (
                    <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setQuoteError(null)}>
                      {quoteError}
                    </Alert>
                  )}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Side</TableCell>
                          <TableCell>Symbol</TableCell>
                          <TableCell align="right">Bid</TableCell>
                          <TableCell align="right">Ask</TableCell>
                          <TableCell align="right">Mid</TableCell>
                          <TableCell align="right">Last</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Chip
                              label={isBuy ? 'BUY' : 'SELL'}
                              size="small"
                              color={isBuy ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                            {sym}
                          </TableCell>
                          <TableCell align="right" sx={{ color: q?.bid ? 'text.primary' : 'text.disabled' }}>
                            {q?.bid ? q.bid.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: q?.ask ? 'text.primary' : 'text.disabled' }}>
                            {q?.ask ? q.ask.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: q?.mid ? 'primary.main' : 'text.disabled' }}>
                            {q?.mid ? q.mid.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: q?.last ? 'text.secondary' : 'text.disabled' }}>
                            {q?.last ? q.last.toFixed(2) : '—'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Order parameters */}
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantity (contracts)"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel>Order Type</InputLabel>
                    <Select
                      value={orderType}
                      label="Order Type"
                      onChange={(e) => setOrderType(e.target.value)}
                    >
                      {ORDER_TYPES.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Limit Price"
                    value={price}
                    onChange={(e) => {
                      setPrice(e.target.value);
                      setAutoUpdatePrice(false);
                    }}
                    disabled={orderType !== 'LIMIT'}
                    placeholder="e.g. 1.50"
                    helperText={orderType === 'LIMIT' ? (autoUpdatePrice ? 'Auto from mid' : 'Manual') : ''}
                    slotProps={orderType === 'LIMIT' ? {
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Tooltip title={autoUpdatePrice ? 'Auto-update from mid. Click to lock manual price.' : 'Manual price locked. Click to auto-update from mid.'}>
                              <IconButton
                                size="small"
                                sx={{ p: 0.5 }}
                                onClick={() => setAutoUpdatePrice(!autoUpdatePrice)}
                              >
                                {autoUpdatePrice ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <IconButton
                                size="small"
                                sx={{ p: 0.25 }}
                                onClick={() => {
                                  const val = parseFloat(price) || 0;
                                  setPrice(String(Math.round((val + 0.01) * 100) / 100));
                                  setAutoUpdatePrice(false);
                                }}
                              >
                                <ArrowDropUpIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ p: 0.25, mt: -1 }}
                                onClick={() => {
                                  const val = parseFloat(price) || 0;
                                  setPrice(String(Math.round((val - 0.01) * 100) / 100));
                                  setAutoUpdatePrice(false);
                                }}
                              >
                                <ArrowDropDownIcon />
                              </IconButton>
                            </Box>
                          </InputAdornment>
                        ),
                      }
                    } : undefined}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel>Time in Force</InputLabel>
                    <Select
                      value={timeInForce}
                      label="Time in Force"
                      onChange={(e) => setTimeInForce(e.target.value)}
                    >
                      {TIME_IN_FORCE.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Action buttons */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handlePreview}
                  disabled={loading}
                >
                  Preview Order
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  onClick={handlePlaceOrder}
                  disabled={loading || !selectedAccountId}
                >
                  Place Order
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Preview */}
          {previewSpec && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  Order Preview (sent to Robinhood MCP)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', overflowX: 'auto' }}>
                  <pre style={{ fontSize: '0.85rem', margin: 0 }}>
                    {JSON.stringify(previewSpec, null, 2)}
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          )}
        </Grid>

        {/* Right column: Recent orders */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Recent Orders
                </Typography>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={fetchRecentOrders} disabled={ordersLoading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {ordersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : recentOrders.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                  No recent orders found
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Symbol</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Entered</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentOrders.slice(0, 20).map((order) => {
                        const orderId = order.orderId || '';
                        const status = order.status || 'UNKNOWN';
                        const entered = order.enteredTime
                          ? new Date(order.enteredTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '';
                        const isCancellable = status === 'QUEUED' || status === 'UNCONFIRMED' || status === 'CONFIRMED' || status === 'PENDING';
                        return (
                          <TableRow key={orderId}>
                            <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              {String(orderId).slice(0, 12)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {order.chainSymbol || order.symbol || '—'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={status}
                                size="small"
                                color={
                                  status === 'FILLED' ? 'success' :
                                  isCancellable ? 'primary' :
                                  status === 'CANCELLED' || status === 'REJECTED' ? 'error' :
                                  'default'
                                }
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {order.price || '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{entered}</TableCell>
                            <TableCell align="right">
                              {isCancellable && (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleCancelOrder(orderId)}
                                >
                                  Cancel
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Info card */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Robinhood MCP Limitations</strong>
              </Typography>
              <Box sx={{ pl: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  - <strong>Single-leg only:</strong> No spreads, strangles, or iron condors
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  - <strong>Agentic account:</strong> Only the funded Agentic account can trade
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  - <strong>Quotes:</strong> Fetched from Schwab (Robinhood MCP quotes require instrument IDs)
                </Typography>
                <Typography variant="body2">
                  - <strong>OCC Symbol:</strong> UNDERLYING + YYMMDD + C/P + STRIKE(8-digit)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
